import React, { useState, useEffect } from 'react';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import { url } from '../../utils/confserveur';
import { useUser } from '../../context/UserContext';
import { getSlidingYearRange } from '../../utils/sequencesfunction';
import { Calendar, AlertTriangle, CheckCircle, Clock, ChevronRight } from 'lucide-react';

const FormPeriode = ({periode}) => {
  const [periodeType, setPeriodeType] = useState('');
  const [periods, setPeriods] = useState([]);
  const {min, max} = getSlidingYearRange();
  const [errors, setErrors] = useState([])
  const [{data: PeriodePosted, isLoading: isLoadingPost , isError: isErrorPost }, doPost] = usePostApi(url + "periodescolaires/bulk-create/");
  const { etablissementData, refreshEtablissementData } = useUser();


  // Lors du changement du type de période
  const handleTypeChange = (e) => {
    const type = e.target.value;
    setPeriodeType(type);
    if (type === 'trimestre') {
      setPeriods([{ start: '', end: ''},
        { start: '', end: ''  },
        { start: '', end: ''}]);
    } else if (type === 'semestre') {
      setPeriods([{ start: '', end: '' }, { start: '', end: '' }]);
    } else {
      setPeriods([]);
    }
  };

  // Gestion du changement pour une période donnée
  const handlePeriodChange = (index, field, value) => {
    const newPeriods = periods.map((period, i) =>
      i === index ? { ...period, [field]: value } : period
    );
    setPeriods(newPeriods);
  };

  // Vérification complète du formulaire :
  // 1. Tous les champs sont renseignés.
  // 2. Pour chaque période, start < end.
  // 3. Pour les périodes suivantes, la date de début est strictement postérieure à la date de fin de la période précédente.
  useEffect(() => {
    const validationErrors = [];

    if (!periodeType) {
      validationErrors.push("Veuillez sélectionner un type de période.");
    }

    for (let i = 0; i < periods.length; i++) {
      const { start, end } = periods[i];
      if (!start || !end) {
        validationErrors.push(
          `Problème pour ${periodeType} ${i + 1} : veuillez renseigner les deux dates.`
        );
      } else {
        if (new Date(start) >= new Date(end)) {
          validationErrors.push(
            `Problème pour ${periodeType} ${i + 1} : la date de début doit être inférieure à la date de fin.`
          );
        }
        if (i > 0) {
          const prevEnd = periods[i - 1].end;
          if (!prevEnd || new Date(start) <= new Date(prevEnd)) {
            validationErrors.push(
              `Problème pour ${periodeType} ${i + 1} : la date de début doit être postérieure à la date de fin du ${periodeType} ${i}.`
            );
          }
        }
      }
    }

    setErrors(validationErrors);
  }, [periodeType, periods]);

  // Soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();

  // Construction du payload attendu par votre endpoint Django
  const payload = {
    periodes: periods.map((period, index) => ({
      etablissement: etablissementData.id,
      type_periode: periodeType,
      numero: index + 1,
      date_debut: period.start,
      date_fin: period.end,
    })),
  };

  // Utilisation de votre hook pour poster les données
  doPost(payload)
    .then(response => {
      alert("Les périodes ont été enregistrées !");
      refreshEtablissementData();
    }

  )
    .catch(error => {
      console.error('Erreur lors de la soumission:', error);
    });
  };


  // On définit le minimum global (date de début du 1er période)
  // et le maximum global (date de fin du dernier période)
  const globalMin = periode[0]?.start || undefined;
  const globalMax = periode[periode.length - 1]?.end || undefined;

  return (
    <div className="w-full mt-5 flex justify-center">
      <div className="w-fit bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header avec gradient et blobs animés */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-cyan-500 to-sky-400 px-8 xl:px-5 2xl:px-6 3xl:px-8 py-10 xl:py-6 2xl:py-8 3xl:py-10">
          {/* Blobs animés */}
          <svg className="absolute top-0 left-0 w-full h-full opacity-30" viewBox="0 0 200 200">
            <circle cx="20" cy="20" r="40" fill="#67e8f9">
              <animate attributeName="cx" values="20;60;20" dur="8s" repeatCount="indefinite" />
              <animate attributeName="cy" values="20;50;20" dur="6s" repeatCount="indefinite" />
            </circle>
            <circle cx="180" cy="80" r="30" fill="#a5f3fc">
              <animate attributeName="cx" values="180;140;180" dur="7s" repeatCount="indefinite" />
              <animate attributeName="cy" values="80;120;80" dur="9s" repeatCount="indefinite" />
            </circle>
            <circle cx="100" cy="150" r="35" fill="#22d3ee">
              <animate attributeName="cx" values="100;130;100" dur="10s" repeatCount="indefinite" />
              <animate attributeName="cy" values="150;170;150" dur="5s" repeatCount="indefinite" />
            </circle>
          </svg>

          <div className="relative z-10 flex items-center gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
            <div className="p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
              <Calendar className="text-white xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-9 3xl:h-9" size={36} />
            </div>
            <div>
              <h1 className="text-3xl xl:text-xl 2xl:text-2xl 3xl:text-3xl font-bold text-white">Configuration des périodes</h1>
              <p className="text-cyan-100 mt-1 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">Définissez les périodes scolaires de votre établissement</p>
            </div>
          </div>
        </div>

        {/* Contenu du formulaire */}
        <form onSubmit={handleSubmit} className="p-8 xl:p-5 2xl:p-6 3xl:p-8">
          {/* Section info */}
          <div className="mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200/50 rounded-2xl flex items-start gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
            <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-blue-100 rounded-xl shrink-0">
              <Clock size={20} className="text-blue-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
            </div>
            <div>
              <p className="font-semibold text-blue-800 xl:text-sm 2xl:text-sm 3xl:text-base">Information</p>
              <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-blue-600">
                Choisissez entre un découpage en trimestres (3 périodes) ou en semestres (2 périodes)
                puis définissez les dates de début et de fin pour chaque période.
              </p>
            </div>
          </div>

          {/* Erreurs */}
          {errors.length > 0 && periodeType && (
            <div className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6 p-4 xl:p-3 2xl:p-3.5 3xl:p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl">
              <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3">
                <div className="p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-amber-100 rounded-xl">
                  <AlertTriangle size={20} className="text-amber-600 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                </div>
                <p className="font-semibold text-amber-800 xl:text-sm 2xl:text-sm 3xl:text-base">Erreurs détectées</p>
              </div>
              <ul className="space-y-1 pl-12 xl:pl-9 2xl:pl-10 3xl:pl-12">
                {errors.map((error, idx) => (
                  <li key={idx} className="text-amber-700 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                    <ChevronRight size={14} className="text-amber-500 xl:w-3 xl:h-3 2xl:w-3.5 2xl:h-3.5 3xl:w-3.5 3xl:h-3.5" />
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sélection du type de période */}
          <div className="mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8">
            <label htmlFor="periodeType" className="block mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3 text-slate-700 font-semibold text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
              Type de période
            </label>
            <div className="relative">
              <select
                id="periodeType"
                value={periodeType}
                onChange={handleTypeChange}
                className="w-full p-4 xl:p-3 2xl:p-3.5 3xl:p-4 text-lg xl:text-sm 2xl:text-base 3xl:text-lg bg-white/70 backdrop-blur-sm border-2 border-slate-200 rounded-xl
                  focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent
                  appearance-none cursor-pointer transition-all hover:border-cyan-300"
              >
                <option value="">-- Sélectionnez un type --</option>
                <option value="trimestre">Trimestre (3 périodes)</option>
                <option value="semestre">Semestre (2 périodes)</option>
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronRight size={20} className="text-slate-400 rotate-90" />
              </div>
            </div>
          </div>

          {/* Cards des périodes */}
          {periodeType && (
            <div className="space-y-4 xl:space-y-3 2xl:space-y-3.5 3xl:space-y-4 mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8">
              <h3 className="text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold text-slate-700 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                <Calendar size={20} className="text-cyan-500 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
                Définition des {periodeType}s
              </h3>

              <div className="grid gap-4">
                {periods.map((period, index) => (
                  <div
                    key={index}
                    className="group relative p-6 xl:p-4 2xl:p-5 3xl:p-6 bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl
                      shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {/* Glow effect on hover */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl
                      blur opacity-0 group-hover:opacity-20 transition duration-300" />

                    <div className="relative">
                      {/* Header de la période */}
                      <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-5 xl:mb-3 2xl:mb-4 3xl:mb-5">
                        <div className="w-10 h-10 xl:w-8 xl:h-8 2xl:w-9 2xl:h-9 3xl:w-10 3xl:h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500
                          flex items-center justify-center text-white font-bold text-lg xl:text-sm 2xl:text-base 3xl:text-lg shadow-lg shadow-cyan-500/30">
                          {index + 1}
                        </div>
                        <h4 className="text-xl xl:text-base 2xl:text-lg 3xl:text-xl font-semibold text-slate-800">
                          {periodeType === 'trimestre' ? `Trimestre ${index + 1}` : `Semestre ${index + 1}`}
                        </h4>
                      </div>

                      {/* Champs de dates */}
                      <div className="grid md:grid-cols-2 gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
                        <div>
                          <label className="block text-slate-600 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2 font-medium xl:text-sm 2xl:text-sm 3xl:text-base">
                            Date de début
                          </label>
                          <input
                            type="date"
                            value={period.start}
                            onChange={(e) => handlePeriodChange(index, 'start', e.target.value)}
                            min={globalMin}
                            max={globalMax}
                            className="w-full p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-lg xl:text-sm 2xl:text-base 3xl:text-lg bg-slate-50 border-2 border-slate-200 rounded-xl
                              focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent
                              transition-all hover:border-cyan-300"
                          />
                        </div>
                        <div>
                          <label className="block text-slate-600 mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2 font-medium xl:text-sm 2xl:text-sm 3xl:text-base">
                            Date de fin
                          </label>
                          <input
                            type="date"
                            value={period.end}
                            onChange={(e) => handlePeriodChange(index, 'end', e.target.value)}
                            min={globalMin}
                            max={globalMax}
                            className="w-full p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-lg xl:text-sm 2xl:text-base 3xl:text-lg bg-slate-50 border-2 border-slate-200 rounded-xl
                              focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent
                              transition-all hover:border-cyan-300"
                          />
                        </div>
                      </div>

                      {/* Indicateur de validation */}
                      {period.start && period.end && new Date(period.start) < new Date(period.end) && (
                        <div className="mt-4 xl:mt-3 2xl:mt-3.5 3xl:mt-4 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 text-emerald-600">
                          <CheckCircle size={18} className="xl:w-4 xl:h-4 2xl:w-[17px] 2xl:h-[17px] 3xl:w-[18px] 3xl:h-[18px]" />
                          <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium">Période valide</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton de validation */}
          {errors.length === 0 && periodeType && (
            <button
              type="submit"
              disabled={isLoadingPost}
              className="w-full py-4 xl:py-3 2xl:py-3.5 3xl:py-4 px-6 xl:px-4 2xl:px-5 3xl:px-6 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold
                rounded-xl shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40
                transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                flex items-center justify-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3"
            >
              {isLoadingPost ? (
                <>
                  <div className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Enregistrement en cours...
                </>
              ) : (
                <>
                  <CheckCircle size={22} className="xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-[22px] 3xl:h-[22px]" />
                  Valider les périodes
                </>
              )}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default FormPeriode;
